import type { MouseEvent } from "react";
import { Icon } from "../icons/Icon";
import { Button } from "../primitives/Button";
import { cx } from "../utils/format";
import type { Navigate } from "./AppShell";

/* M11, the front door and the first setup: the pieces the web ports from the prototype. Props in, events out. */

export interface SetupChecklistStep {
  id: string; title: string; /** one line: what it is for */ text: string;
  /** the button's words and where it leads (a deep link into the screen that does it) */ action: string; href: string;
  /** read from what is really set up, never from a click on the row */ done: boolean;
  /** a second, quiet link under the line while the step is open (the AI step's "See a sample draft") */ extra?: { label: string; href: string };
}

/** The owner's setup checklist: the steps in order, ticked from the workspace's own state, the progress, and a way to hide it.
 *  The next open step carries the primary button; done steps say so. The caller removes the card once every step is done. */
export function SetupChecklist({ steps, onNavigate, onDismiss, title = "Set up your workspace", intro, tour }: {
  steps: SetupChecklistStep[]; onNavigate: Navigate; /** absent: the card cannot be hidden */ onDismiss?: () => void; title?: string; intro?: string;
  /** the tour's mark on the card (`data-tour`): where a stop points */ tour?: string;
}) {
  const done = steps.filter((s) => s.done).length;
  const next = steps.find((s) => !s.done);
  return (
    <section className="ob-setup-card" aria-label={title} data-tour={tour}>
      <header className="ob-setup-card__head">
        <div className="ob-setup-card__heading">
          <h2 className="ob-setup-card__title">{title}</h2>
          {intro && <p className="ob-setup-card__intro">{intro}</p>}
        </div>
        <span className="ob-setup-card__count">{done} of {steps.length} done</span>
        {onDismiss && <Button variant="icon" size="sm" icon="x" className="ob-setup-card__dismiss" aria-label="Hide the setup checklist" onClick={onDismiss} />}
      </header>
      <div className="ob-setup-card__bar" role="progressbar" aria-label="Setup progress" aria-valuemin={0} aria-valuemax={steps.length} aria-valuenow={done}>
        <span className="ob-setup-card__fill" style={{ width: `${steps.length ? (done / steps.length) * 100 : 0}%` }} />
      </div>
      <ol className="ob-setup-card__list" aria-label="Setup steps">
        {steps.map((s, i) => (
          <li key={s.id} className={cx("ob-setup-card__step", s.done && "ob-setup-card__step--done")}>
            <span className={cx("ob-setup-card__mark", s === next && "ob-setup-card__mark--next")} aria-hidden="true">{s.done ? <Icon name="check" size="sm" /> : i + 1}</span>
            <span className="ob-setup-card__text"><span className="ob-setup-card__step-title">{s.title}</span><span className="ob-setup-card__step-text">{s.text}</span>
              {s.extra && !s.done && <a className="ob-link ob-setup-card__extra" href={s.extra.href} onClick={(e: MouseEvent<HTMLAnchorElement>) => onNavigate(s.extra!.href, e)}>{s.extra.label}</a>}</span>
            {s.done ? <span className="ob-setup-card__done">Done</span>
              : <a className={cx("ob-btn", "ob-btn--sm", s === next ? "ob-btn--primary" : "ob-btn--secondary", "ob-setup-card__go")} href={s.href} onClick={(e: MouseEvent<HTMLAnchorElement>) => onNavigate(s.href, e)}>{s.action}</a>}
          </li>
        ))}
      </ol>
    </section>
  );
}

/** One invitation to a workspace: who invited you where, with Decline and Accept; an expired one says whom to ask instead. */
export function InvitationRow({ mark, workspace, inviter, role, expired, onAccept, onDecline }: {
  mark: string; workspace: string; inviter: string; /** "Agent", "Administrator" */ role?: string; expired?: boolean;
  onAccept?: () => void; onDecline?: () => void;
}) {
  return (
    <li className={cx("ob-invite-row", expired && "ob-invite-row--expired")}>
      <span className="ob-mark" aria-hidden="true">{mark}</span>
      <span className="ob-invite-row__text">
        <span className="ob-invite-row__title">{workspace}</span>
        <span className="ob-invite-row__meta">{expired ? `Your invitation expired. Ask ${inviter} for a new one.` : `${inviter} invited you${role ? ` as ${role.toLowerCase()}` : ""}`}</span>
      </span>
      {!expired && (
        <span className="ob-invite-row__actions">
          <Button size="sm" onClick={onDecline} aria-label={`Decline the invitation to ${workspace}`}>Decline</Button>
          <Button size="sm" variant="primary" onClick={onAccept} aria-label={`Accept the invitation to ${workspace}`}>Accept</Button>
        </span>
      )}
    </li>
  );
}

/** "Sample": the made-up ticket, its customer and its draft carry it wherever they are listed. Plain, never the AI hue. */
export function SampleChip({ className }: { className?: string }) {
  return <span className={cx("ob-sample-chip", className)}>Sample</span>;
}
